import { useState, useRef, useEffect, PropsWithChildren } from 'react';
import styles from './ContentExpander.module.scss';
import { Spacer } from '@components/index';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ArrowIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import { TObject } from '@types';

type TProps = {
	title: JSX.Element[] | JSX.Element | string;
	contentHasPadding?: boolean;
	expanded?: null | boolean;
	toggleExpanded?: (b: boolean) => void;
	spacerOnTop?: boolean;
	showSpacer?: boolean | 'WHEN_CLOSED';
	arrowsAsExpanderIcon?: boolean;
	iconColor?: string;
}

export const ContentExpander = (props: PropsWithChildren<TProps>) => {
	const {
		title,
		contentHasPadding = false,
		expanded = true,
		toggleExpanded,
		children,
		spacerOnTop = false,
		showSpacer = true,
		arrowsAsExpanderIcon = true,
		iconColor = 'var(--brand-orange-default)'
	} = props;
	const [hasMounted, setHasMounted] = useState(false);
	const [isExpanded, setIsExpanded] = useState(expanded);
	const [, setChildrenHeight] = useState<number>(0);
	const childrenRef = useRef<HTMLDivElement>(null);
	const onExpand = () => {
		setIsExpanded(!isExpanded);
		if (toggleExpanded) toggleExpanded(!isExpanded);
	}
	const getHeight = () => {
		return !isExpanded ? '0px' : 'auto';
	}
	const setStyles = () => {
		const styles: TObject = {};
		styles.paddingTop = contentHasPadding ? '10px' : '0px';
		styles.paddingBottom = contentHasPadding ? '10px' : '0px';
		styles.paddingLeft = contentHasPadding ? '10px' : '0px';
		styles.height = getHeight();
		return styles;
	}
	const reCalcHeight = () => {
		if (childrenRef && childrenRef.current) {
			let childHeight = childrenRef.current.offsetHeight;
			if (contentHasPadding) {
				childHeight = childHeight + 20;
			}
			setChildrenHeight(childHeight);
			if (!hasMounted) setHasMounted(true);
		}
	}

	useEffect(() => {
		reCalcHeight();
	}, [childrenRef.current]);
	useEffect(() => {
		if (expanded !== null) {
			reCalcHeight();
			setIsExpanded(expanded);
		}
	}, [expanded]);

	return (
		<div className={styles.container}>
			<button onClick={onExpand} className={styles.titleContainer} aria-label={`Expander Toggle ${title}`}>
				<div className={styles.title}>
					{title}
				</div>
				{!arrowsAsExpanderIcon && <>
					{isExpanded ?
						<RemoveIcon style={{ color: iconColor }} /> :
						<AddIcon style={{ color: iconColor }} />}
				</>}
				{arrowsAsExpanderIcon && <ArrowIcon className={`${styles.arrow} ${isExpanded ? styles.arrowTwist : ''}`} style={{ color: iconColor, cursor: 'pointer' }} />}
			</button>
			{spacerOnTop && (showSpacer === true || (showSpacer === 'WHEN_CLOSED' && !isExpanded)) && <Spacer size={10} divider />}
			<div
				className={`${styles.children} ${!isExpanded && styles.collapsed}`}
				style={setStyles()}
			>
				<div ref={childrenRef}>{children}</div>
			</div>
			{!spacerOnTop && (showSpacer === true || (showSpacer === 'WHEN_CLOSED' && !isExpanded)) && <Spacer size={10} divider />}
		</div>
	);
}

export const Expander = ContentExpander;
